import { useMemo } from 'react'

import { useAuth } from '@/auth/use-auth'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * The permission codes the loan endpoints authorise against.
 *
 * Every one is taken from the generated catalogue, so nothing here is a hand-written guess. Two
 * of them do not begin with `loans.` and that is deliberate rather than an oversight: ageing is
 * the operations module's nightly job (`ops.jobs.trigger`) and classification posts a provision
 * movement into the ledger (`ledger.provision.run`). The server checks those codes, not a loans
 * one, so a screen that gated them on `loans.view` would show a button that always answered 403.
 *
 * The `.approve` half of each pair is listed beside its action because it is what lets a screen
 * say, before the click, whether the operator's own role can countersign. That is a statement
 * about the *user* and never about the outcome — the approval policy is the server's, and it may
 * still demand a second person.
 */
export const LOAN_PERMISSIONS = {
  view: PERMISSIONS.loans.view,
  accountOpen: PERMISSIONS.loans.accountOpen,
  apply: PERMISSIONS.loans.apply,
  appraise: PERMISSIONS.loans.appraise,
  approve: PERMISSIONS.loans.approve,
  approveCountersign: PERMISSIONS.loans.approveCountersign,
  disburse: PERMISSIONS.loans.disburse,
  disburseApprove: PERMISSIONS.loans.disburseApprove,
  repaymentPost: PERMISSIONS.loans.repaymentPost,
  penaltyWaive: PERMISSIONS.loans.penaltyWaive,
  penaltyWaiveApprove: PERMISSIONS.loans.penaltyWaiveApprove,
  restructure: PERMISSIONS.loans.restructure,
  restructureApprove: PERMISSIONS.loans.restructureApprove,
  writeOff: PERMISSIONS.loans.writeOff,
  writeOffApprove: PERMISSIONS.loans.writeOffApprove,
  guaranteeOffset: PERMISSIONS.loans.guaranteeOffset,
  guaranteeOffsetApprove: PERMISSIONS.loans.guaranteeOffsetApprove,
  classificationOverride: PERMISSIONS.loans.classificationOverride,
  classificationOverrideApprove: PERMISSIONS.loans.classificationOverrideApprove,
  classificationConfig: PERMISSIONS.loans.classificationConfig,
  classificationConfigApprove: PERMISSIONS.loans.classificationConfigApprove,
  scheduleView: PERMISSIONS.loans.scheduleView,
  statementView: PERMISSIONS.loans.statementView,
  disclosureGenerate: PERMISSIONS.loans.disclosureGenerate,
  disclosureConfig: PERMISSIONS.loans.disclosureConfig,
  disclosureConfigApprove: PERMISSIONS.loans.disclosureConfigApprove,
  productView: PERMISSIONS.loans.productView,
  productManage: PERMISSIONS.loans.productManage,
  productManageApprove: PERMISSIONS.loans.productManageApprove,
  /** Ageing is the nightly job triggered by hand — the operations catalogue owns the code. */
  ageingRun: PERMISSIONS.ops.jobsTrigger,
  /** Classification posts the provision movement, so the ledger's catalogue owns the code. */
  provisionRun: PERMISSIONS.ledger.provisionRun,
} as const

export interface LoanPermissions {
  canView: boolean
  canOpenAccount: boolean
  canApply: boolean
  canAppraise: boolean
  canApprove: boolean
  canDisburse: boolean
  canPostRepayment: boolean
  canWaivePenalty: boolean
  canRestructure: boolean
  canWriteOff: boolean
  canOffsetGuarantee: boolean
  canOverrideClassification: boolean
  canConfigureClassification: boolean
  canViewSchedule: boolean
  canViewStatement: boolean
  canGenerateDisclosure: boolean
  canConfigureDisclosure: boolean
  canViewProducts: boolean
  canManageProducts: boolean
  canRunAgeing: boolean
  canRunClassification: boolean

  /** Whether this user also holds the countersigning half of each dual-controlled action. */
  canCountersignApproval: boolean
  canCountersignDisbursement: boolean
  canCountersignWaiver: boolean
  canCountersignRestructure: boolean
  canCountersignWriteOff: boolean
  canCountersignOffset: boolean
  canCountersignOverride: boolean
  canCountersignProduct: boolean
  canCountersignScheme: boolean
  canCountersignDisclosureConfig: boolean
}

/**
 * What this user may do with a loan. Presentation only: the server authorises every command
 * against the live catalogue, so a visible button is not an open door and a hidden one is not a
 * closed door.
 */
export function useLoanPermissions(): LoanPermissions {
  const { user } = useAuth()
  const permissions = user?.permissions

  return useMemo(() => {
    const holds = (code: string) => permissions?.some((held) => held === code) ?? false

    return {
      canView: holds(LOAN_PERMISSIONS.view),
      canOpenAccount: holds(LOAN_PERMISSIONS.accountOpen),
      canApply: holds(LOAN_PERMISSIONS.apply),
      canAppraise: holds(LOAN_PERMISSIONS.appraise),
      canApprove: holds(LOAN_PERMISSIONS.approve),
      canDisburse: holds(LOAN_PERMISSIONS.disburse),
      canPostRepayment: holds(LOAN_PERMISSIONS.repaymentPost),
      canWaivePenalty: holds(LOAN_PERMISSIONS.penaltyWaive),
      canRestructure: holds(LOAN_PERMISSIONS.restructure),
      canWriteOff: holds(LOAN_PERMISSIONS.writeOff),
      canOffsetGuarantee: holds(LOAN_PERMISSIONS.guaranteeOffset),
      canOverrideClassification: holds(LOAN_PERMISSIONS.classificationOverride),
      canConfigureClassification: holds(LOAN_PERMISSIONS.classificationConfig),
      canViewSchedule: holds(LOAN_PERMISSIONS.scheduleView),
      canViewStatement: holds(LOAN_PERMISSIONS.statementView),
      canGenerateDisclosure: holds(LOAN_PERMISSIONS.disclosureGenerate),
      canConfigureDisclosure: holds(LOAN_PERMISSIONS.disclosureConfig),
      canViewProducts: holds(LOAN_PERMISSIONS.productView),
      canManageProducts: holds(LOAN_PERMISSIONS.productManage),
      canRunAgeing: holds(LOAN_PERMISSIONS.ageingRun),
      canRunClassification: holds(LOAN_PERMISSIONS.provisionRun),

      canCountersignApproval: holds(LOAN_PERMISSIONS.approveCountersign),
      canCountersignDisbursement: holds(LOAN_PERMISSIONS.disburseApprove),
      canCountersignWaiver: holds(LOAN_PERMISSIONS.penaltyWaiveApprove),
      canCountersignRestructure: holds(LOAN_PERMISSIONS.restructureApprove),
      canCountersignWriteOff: holds(LOAN_PERMISSIONS.writeOffApprove),
      canCountersignOffset: holds(LOAN_PERMISSIONS.guaranteeOffsetApprove),
      canCountersignOverride: holds(LOAN_PERMISSIONS.classificationOverrideApprove),
      canCountersignProduct: holds(LOAN_PERMISSIONS.productManageApprove),
      canCountersignScheme: holds(LOAN_PERMISSIONS.classificationConfigApprove),
      canCountersignDisclosureConfig: holds(LOAN_PERMISSIONS.disclosureConfigApprove),
    }
  }, [permissions])
}
